import { Dispatch, SetStateAction } from 'react'

import useSWR from 'swr'
import useSWRMutation from 'swr/mutation'

import { apiDefaultFetcher, apiGetBlobFetcher } from '@/common/api/fetcher'
import { DEFAULT_PAGE, DEFAULT_PER_PAGE } from '@/common/api/pagination'
import {
  AuditLogApiPath,
  ListAuditLogsResponse,
  ListAuditLogsVars
} from '@/common/api/params/auditLog'
import { SearchConditionBase, SearchConditionParam, useSearchCondition } from '@/common/api/search'
import { useShowToastIfMutationError } from '@/common/app/toast'

export interface AuditLogSearchCondition extends SearchConditionBase {
  action: string | null
  operatorName: string | null
  dateFrom: string | null
  dateTo: string | null
}

const auditLogSearchConditionParam: SearchConditionParam[] = [
  { name: 'action', type: 'string' },
  { name: 'operatorName', type: 'string' },
  { name: 'dateFrom', type: 'string' },
  { name: 'dateTo', type: 'string' }
]

export const AUDIT_LOG_SEARCH_CONDITION_CLEAR: AuditLogSearchCondition = {
  page: DEFAULT_PAGE,
  perPage: DEFAULT_PER_PAGE,
  action: null,
  operatorName: null,
  dateFrom: null,
  dateTo: null
}

export const useAuditLogSearchCondition = (): [
  AuditLogSearchCondition,
  Dispatch<SetStateAction<AuditLogSearchCondition>>,
  (page: number) => void
] => {
  return useSearchCondition<AuditLogSearchCondition>(auditLogSearchConditionParam)
}

const buildVars = (condition: AuditLogSearchCondition): ListAuditLogsVars => ({
  page: condition.page,
  per_page: condition.perPage,
  ...(condition.action && { action: condition.action }),
  ...(condition.operatorName && { operator_name: condition.operatorName }),
  ...(condition.dateFrom && { date_from: condition.dateFrom }),
  ...(condition.dateTo && { date_to: condition.dateTo })
})

export const useAuditLogSearchApi = (condition: AuditLogSearchCondition) => {
  return useSWR<ListAuditLogsResponse, Response, [string, ListAuditLogsVars]>(
    [AuditLogApiPath.LIST_AUDIT_LOGS, buildVars(condition)],
    apiDefaultFetcher
  )
}

export const useDownloadAuditLogsCsv = (onSuccess: (blob: Blob) => void) => {
  const showError = useShowToastIfMutationError()
  return useSWRMutation<Blob, Response, string, ListAuditLogsVars>(
    AuditLogApiPath.EXPORT_AUDIT_LOGS_CSV,
    apiGetBlobFetcher,
    { onSuccess, onError: showError }
  )
}
